import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Table as TableIcon, SquaresFour, ArrowLeft, FileText } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { CartActionsBar } from "@/components/cart/CartActionsBar";
import { CartItemsTable } from "@/components/cart/CartItemsTable";
import { CartItemsGrid } from "@/components/cart/CartItemsGrid";
import { QuotesListPanel } from "@/components/cart/QuotesListPanel";
import { NewCartDialog } from "@/components/carts/NewCartDialog";
import { RenameCartDialog } from "@/components/carts/RenameCartDialog";
import { DeleteCartDialog } from "@/components/carts/DeleteCartDialog";
import { GridLayoutPickerDialog } from "@/components/cart/GridLayoutPickerDialog";
import { canSeeGT } from "@/lib/fmUtils";

export default function Cart() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const isCustomer = user?.role === "customer";
  const isSales = user?.role === "sales" || user?.role === "gt_sales";
  const showGTExport = canSeeGT(user);

  const [items, setItems] = useState([]);
  const [cartId, setCartId] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [coefficient, setCoefficient] = useState(1);
  const [currency, setCurrency] = useState("CAD");
  const [linkedCustomerId, setLinkedCustomerId] = useState(null);
  const [isGT, setIsGT] = useState(false);
  const [createdAt, setCreatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [emailingQuote, setEmailingQuote] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState(new Set());
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("cartViewMode") || "table");

  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [gridLayoutOpen, setGridLayoutOpen] = useState(false);
  const [newQuoteName, setNewQuoteName] = useState("");
  const [newQuoteCustomerId, setNewQuoteCustomerId] = useState("none");
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [editCustomerId, setEditCustomerId] = useState("none");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem("cartViewMode", mode);
  };

  const fetchCart = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cart");
      setItems(data.items);
      setCartId(data.cart_id);
      setCustomerName(data.customer_name || "");
      setCoefficient(data.coefficient);
      setCurrency(data.currency || "CAD");
      setLinkedCustomerId(data.customer_id || null);
      setIsGT(!!data.is_gt);
      setCreatedAt(data.created_at || null);
    } catch (e) {
      toast({
        title: "Failed to load quote",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchQuotes = useCallback(async () => {
    try {
      const { data } = await api.get("/carts");
      setQuotes(data);
    } catch {
      /* list panel just stays stale */
    }
  }, []);

  useEffect(() => {
    fetchCart();
    fetchQuotes();
  }, [fetchCart, fetchQuotes]);

  useEffect(() => {
    if (isCustomer) return;
    api
      .get("/customers")
      .then(({ data }) => setCustomers(data))
      .catch(() => {});
  }, [isCustomer]);

  const handleSelectQuote = async (quote) => {
    if (quote.id === cartId) return;
    try {
      await api.put(`/carts/${quote.id}/activate`);
      setSelectedVariants(new Set());
      await Promise.all([fetchCart(), fetchQuotes()]);
    } catch (e) {
      toast({
        title: "Failed to open quote",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    }
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    if (!newQuoteName.trim()) return;
    setSaving(true);
    try {
      await api.post("/carts", {
        customer_name: newQuoteName.trim(),
        customer_id: newQuoteCustomerId === "none" ? null : newQuoteCustomerId,
      });
      toast({ title: "Quote created" });
      setNewQuoteName("");
      setNewQuoteCustomerId("none");
      setCreateOpen(false);
      setSelectedVariants(new Set());
      await Promise.all([fetchCart(), fetchQuotes()]);
    } catch (e) {
      toast({
        title: "Failed to create quote",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const openRename = (quote) => {
    setRenameTarget(quote);
    setRenameValue(quote.customer_name);
    setEditCustomerId(quote.customer_id || "none");
  };

  const handleRename = async (e) => {
    e.preventDefault();
    if (!renameValue.trim() || !renameTarget) return;
    setSaving(true);
    try {
      await api.patch(`/carts/${renameTarget.id}`, { customer_name: renameValue.trim() });
      const newCustomerId = editCustomerId === "none" ? null : editCustomerId;
      if (!isCustomer && newCustomerId !== (renameTarget.customer_id || null)) {
        await api.put(`/carts/${renameTarget.id}/customer`, { customer_id: newCustomerId });
      }
      toast({ title: "Quote updated" });
      setRenameTarget(null);
      await Promise.all([fetchCart(), fetchQuotes()]);
    } catch (e) {
      toast({
        title: "Update failed",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuote = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/carts/${deleteTarget.id}`);
      toast({ title: "Quote deleted" });
      setDeleteTarget(null);
      await Promise.all([fetchCart(), fetchQuotes()]);
    } catch (e) {
      toast({
        title: "Delete failed",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
      setDeleteTarget(null);
    }
  };

  const handleRemove = async (itemId) => {
    try {
      await api.delete(`/cart/items/${itemId}`);
      setItems((prev) => prev.filter((i) => i.id !== itemId));
      toast({ title: "Removed from quote" });
      fetchQuotes();
    } catch (e) {
      toast({
        title: "Failed to remove item",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const response = await api.get(`/cart/export/${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const filenames = { pdf: "quotation.pdf", excel: "quotation.xlsx", proforma: "proforma_invoice.pdf" };
      link.setAttribute("download", filenames[format] || "export.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let detail;
      try {
        const errBlob = e.response?.data;
        if (errBlob instanceof Blob) {
          const errText = await errBlob.text();
          detail = JSON.parse(errText)?.detail;
        } else {
          detail = e.response?.data?.detail;
        }
      } catch (_) {
        detail = undefined;
      }
      toast({
        title: "Export failed",
        description:
          formatApiErrorDetail(detail) ||
          "Quote may be empty, or an error occurred generating the file.",
        variant: "destructive",
      });
    } finally {
      setExporting(null);
    }
  };

  const handleExportGT = async () => {
    const selections = Array.from(selectedVariants).map((key) => {
      const [item_id, variant_record_id] = key.split("::");
      return { item_id, variant_record_id };
    });
    setExporting("gt");
    try {
      const response = await api.post("/cart/export/gt-offer-sheet", { selections }, { responseType: "blob" });
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : "gt_offer_sheets.zip";
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // When responseType is 'blob', axios wraps error bodies as a Blob too - we need to
      // read the Blob's text and parse the JSON to surface the real "detail" message
      // (e.g. "All selected products are blocked and were excluded") instead of a
      // meaningless generic "failed" toast.
      let detail;
      try {
        const errBlob = e.response?.data;
        if (errBlob instanceof Blob) {
          const errText = await errBlob.text();
          const parsed = JSON.parse(errText);
          detail = parsed?.detail;
        } else {
          detail = e.response?.data?.detail;
        }
      } catch (_) {
        detail = undefined;
      }
      toast({
        title: "GT Offer Sheet export failed",
        description: formatApiErrorDetail(detail) || e.message || "Unknown error",
        variant: "destructive",
      });
    } finally {
      setExporting(null);
    }
  };

  const handleEmailQuote = async () => {
    if (!cartId) return;
    setEmailingQuote(true);
    try {
      const { data } = await api.post(`/carts/${cartId}/email-quote`);
      toast({
        title: "Quote email sent",
        description: `Sent to ${data.sent_to}. Anyone with the link can fill in quantities without affecting this quote.`,
      });
    } catch (e) {
      toast({
        title: "Failed to send quote email",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setEmailingQuote(false);
    }
  };

  const toggleVariantSelection = (itemId, variantRecordId) => {
    const key = `${itemId}::${variantRecordId}`;
    setSelectedVariants((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allVariantKeys = items.flatMap((item) =>
    (item.variants || []).filter((v) => v.variant_record_id).map((v) => `${item.id}::${v.variant_record_id}`)
  );
  const allVariantsSelected = allVariantKeys.length > 0 && allVariantKeys.every((k) => selectedVariants.has(k));
  const toggleSelectAllVariants = () => {
    setSelectedVariants(allVariantsSelected ? new Set() : new Set(allVariantKeys));
  };

  const handleVariantFieldBlur = async (itemId, variantRecordId, field, event, fallback) => {
    const rawValue = event.target.value;
    const parsed = field === "carton_qty" ? parseInt(rawValue, 10) : parseFloat(rawValue);
    if (Number.isNaN(parsed) || parsed < 0) {
      toast({ title: "Invalid value", variant: "destructive" });
      event.target.value = fallback ?? "";
      return;
    }
    if (parsed === fallback) return;
    try {
      await api.patch(`/cart/items/${itemId}/variants/${variantRecordId}`, { [field]: parsed });
      setItems((prev) =>
        prev.map((item) =>
          item.id !== itemId
            ? item
            : {
                ...item,
                variants: item.variants.map((v) =>
                  v.variant_record_id === variantRecordId ? { ...v, [field]: parsed } : v
                ),
              }
        )
      );
    } catch (e) {
      event.target.value = fallback ?? "";
      toast({
        title: "Failed to update",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    }
  };

  const linkedCustomer = customers.find((c) => c.id === linkedCustomerId);

  return (
    <div className="flex h-screen w-screen flex-col overflow-x-hidden bg-white" data-testid="cart-page">
      <header className="flex items-center gap-3 border-b border-zinc-300 px-4 py-3 lg:px-6">
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm border-zinc-300"
          onClick={() => navigate("/")}
          data-testid="back-to-dashboard-button"
        >
          <ArrowLeft size={14} className="mr-1.5" /> Dashboard
        </Button>
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-sm border border-zinc-300 bg-zinc-50">
            <FileText size={16} weight="bold" className="text-blue-600" />
          </div>
          <h1 className="font-heading text-lg font-bold tracking-tight text-zinc-950">Quotes</h1>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
        <QuotesListPanel
          quotes={quotes}
          activeId={cartId}
          canDelete={quotes.length > 1}
          onSelect={handleSelectQuote}
          onNew={() => setCreateOpen(true)}
          onRename={openRename}
          onDeleteRequest={setDeleteTarget}
          showCustomerFilter={!isCustomer}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-col gap-3 border-b border-zinc-200 px-4 py-3 lg:px-6">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-950" data-testid="quote-title">
                {customerName || "-"}
              </h2>
              <span
                className="rounded-sm border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-xs font-bold text-zinc-700"
                data-testid="quote-currency-badge"
              >
                {currency}
              </span>
              {!isCustomer && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-medium uppercase tracking-[0.05em] text-zinc-400">Customer</span>
                  <span className="text-sm font-medium text-zinc-800" data-testid="quote-customer-name">
                    {linkedCustomer ? linkedCustomer.name : "No customer"}
                  </span>
                  {linkedCustomer && (
                    <span className="text-[11px] text-zinc-500" data-testid="quote-pricing-summary">
                      Coeff {linkedCustomer.coefficient} · Freight ${Number(linkedCustomer.price_per_skid || 0).toFixed(2)}/skid
                    </span>
                  )}
                </div>
              )}
              <span className="text-xs text-zinc-400" data-testid="quote-date">
                {createdAt ? new Date(createdAt).toLocaleDateString() : ""}
              </span>
            </div>
            <CartActionsBar
              itemCount={items.length}
              exporting={exporting}
              emailingQuote={emailingQuote}
              selectedVariantCount={selectedVariants.size}
              onExport={handleExport}
              onEmailQuote={handleEmailQuote}
              onExportGT={handleExportGT}
              onExportGridView={() => setGridLayoutOpen(true)}
              onPrintLabels={() => navigate("/cart/labels")}
              isCustomer={isCustomer}
              isSales={isSales}
              showGTExport={showGTExport}
            />
          </div>

          <div className="min-w-0 flex-1 overflow-auto p-4 lg:p-6">
            <div className="mb-3 flex items-center">
              <div className="flex items-center rounded-sm border border-zinc-300 bg-zinc-50 p-0.5" data-testid="cart-view-mode-toggle">
                <button
                  type="button"
                  onClick={() => handleViewModeChange("table")}
                  className={`rounded-sm px-2 py-1.5 transition-colors duration-150 ${
                    viewMode === "table" ? "bg-blue-600 text-white" : "text-zinc-500 hover:bg-zinc-100"
                  }`}
                  data-testid="cart-view-mode-table-button"
                >
                  <TableIcon size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => handleViewModeChange("grid")}
                  className={`rounded-sm px-2 py-1.5 transition-colors duration-150 ${
                    viewMode === "grid" ? "bg-blue-600 text-white" : "text-zinc-500 hover:bg-zinc-100"
                  }`}
                  data-testid="cart-view-mode-grid-button"
                >
                  <SquaresFour size={14} />
                </button>
              </div>
            </div>
            {loading ? (
              <div className="text-sm text-zinc-500">Loading...</div>
            ) : viewMode === "grid" ? (
              <CartItemsGrid items={items} onRemove={handleRemove} coefficient={coefficient} pricePerSkid={linkedCustomer?.price_per_skid || 0} showGtSku={isGT} />
            ) : (
              <CartItemsTable
                items={items}
                coefficient={coefficient}
                pricePerSkid={linkedCustomer?.price_per_skid || 0}
                showGtSku={isGT}
                selectedVariants={selectedVariants}
                allVariantsSelected={allVariantsSelected}
                onToggleSelectAllVariants={toggleSelectAllVariants}
                onToggleVariant={toggleVariantSelection}
                onRemove={handleRemove}
                onVariantFieldBlur={handleVariantFieldBlur}
              />
            )}
          </div>
        </div>
      </div>

      <NewCartDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        name={newQuoteName}
        onNameChange={setNewQuoteName}
        saving={saving}
        onSubmit={handleCreateQuote}
        customers={isCustomer ? null : customers}
        customerId={newQuoteCustomerId}
        onCustomerChange={setNewQuoteCustomerId}
      />

      <RenameCartDialog
        target={renameTarget}
        value={renameValue}
        onValueChange={setRenameValue}
        saving={saving}
        onOpenChange={(v) => !v && setRenameTarget(null)}
        onSubmit={handleRename}
        customers={isCustomer ? null : customers}
        customerId={editCustomerId}
        onCustomerChange={setEditCustomerId}
      />

      <DeleteCartDialog
        target={deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        onConfirm={handleDeleteQuote}
      />

      <GridLayoutPickerDialog
        open={gridLayoutOpen}
        onOpenChange={setGridLayoutOpen}
        onPick={(layoutKey) => {
          sessionStorage.setItem(
            "cartGridPrintData",
            JSON.stringify({
              items: items.filter((i) => !i.blocked),
              coefficient,
              showGtSku: isGT,
              layout: layoutKey,
            })
          );
          navigate("/cart/grid-print");
        }}
      />
    </div>
  );
}
